import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    OnChanges, OnInit,
    Optional,
    PLATFORM_ID,
    SimpleChanges,
    ViewChild
} from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
// import './photoswipe.d.ts';
import PhotoSwipeLightbox from "photoswipe/lightbox";
import { DOCUMENT, isPlatformServer, NgIf, NgClass, NgFor, AsyncPipe } from "@angular/common";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";

import { faChevronLeft, faChevronRight, faImage, faPlayCircle } from "@fortawesome/free-solid-svg-icons";
import { Asset } from "../../../common/gql/graphql";
import { notNullOrUndefined } from "../../../common/utils/not-null-or-undefined";
import { extractYoutubeId } from "../../common/extract-youtube-id";
import { PictureComponent } from "../../../shared/components/picture/picture.component";
import { merge, Observable, Subject, switchMap, timer } from "rxjs";
import { distinctUntilChanged, map, tap } from "rxjs/operators";

export type AssetWithDimensions = Pick<Asset, "id" | "preview" | "width" | "height" | "focalPoint">;

@Component({
    selector: "kb-asset-gallery",
    templateUrl: "./asset-gallery.component.html",
    styleUrls: ["./asset-gallery.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgIf, NgClass, NgFor, PictureComponent, FontAwesomeModule, AsyncPipe]
})
export class AssetGalleryComponent implements OnInit, OnChanges {
    @Input() videoUrls: string[] | null = [];
    @Input() assets: AssetWithDimensions[] = [];
    @Input() selectedAssetId: string;
    @Input() productName?: string;
    assetsAndVideos: Array<AssetWithDimensions | string>;
    selectedItem: AssetWithDimensions | string | undefined;
    selectedAssetIsTall = false;

    playCircle = faPlayCircle;
    image = faImage;
    chevronLeft = faChevronLeft;
    chevronRight = faChevronRight;
    isLoadingAsset$: Observable<boolean>;
    private _isLoadingAsset = new Subject<boolean>();

    private lightbox: typeof PhotoSwipeLightbox;
    private browserSupportsAvif = false;

    get selectedAsset(): AssetWithDimensions | undefined {
        return typeof this.selectedItem !== "string" ? this.selectedItem : undefined;
    }

    get selectedVideoUrl(): SafeResourceUrl | undefined {
        return typeof this.selectedItem === "string" ? this.getYouTubeEmbedUrl(this.selectedItem) : undefined;
    }

    @ViewChild("mainPreview", { read: ElementRef })
    private mainPreview: ElementRef<HTMLImageElement>;
    private gallery: any;

    @ViewChild("photoswipeModal", { static: true }) modal: ElementRef<HTMLDivElement>;

    constructor(
        private domSanitizer: DomSanitizer,
        private changeDetector: ChangeDetectorRef,
        @Optional() @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId: any
    ) {
        this.checkAvifSupport();
    }

    ngOnInit() {
        this.isLoadingAsset$ = merge(this._isLoadingAsset.asObservable(),
            this._isLoadingAsset.asObservable().pipe(
                switchMap(() => timer(5000).pipe(map(() => false)))
            )).pipe(
            distinctUntilChanged()
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        const selectedAsset =
            (this.selectedAssetId && this.assets.find((a) => a.id === this.selectedAssetId)) ??
            this.assets[0];

        if (changes["productName"]) {
            this._isLoadingAsset.next(true);
        }

        this.selectItem(selectedAsset);
        const videoIds = (this.videoUrls ?? []).map(extractYoutubeId).filter(notNullOrUndefined);
        this.assetsAndVideos = [...this.assets, ...videoIds];
    }

    isVideoId(item: AssetWithDimensions | string): item is string {
        return typeof item === "string";
    }

    selectItem(item: AssetWithDimensions | string | undefined) {
        this.selectedItem = item;
        if (this.selectedItem) {
            const selector = `[data-asset-id="${typeof item === "string" ? item : item?.id}"]`;
            const el = this.document?.querySelector(selector);
            el?.scrollIntoView?.({ behavior: "smooth", block: "nearest", inline: "start" });
        }
    }

    nextImage() {
        const index = this.assetsAndVideos.findIndex((a) => a === this.selectedItem) ?? 0;
        const nextIndex = (index + 1) % this.assetsAndVideos.length;
        const nextAsset = this.assetsAndVideos[nextIndex];
        this.selectItem(nextAsset);
    }

    previousImage() {
        const index = this.assetsAndVideos.findIndex((a) => a === this.selectedItem) ?? 0;
        const nextIndex = index === 0 ? this.assetsAndVideos.length - 1 : index - 1;
        const nextAsset = this.assetsAndVideos[nextIndex];
        this.selectItem(nextAsset);
    }

    async openImage(assetId: string) {
        const index = this.assetsAndVideos.findIndex((a) =>
            typeof a === "string" ? a === assetId : a.id === assetId
        );
        const items = this.assetsAndVideos.map((asset) => {
            if (typeof asset === "string") {
                return { youtubeId: asset };
            }
            const format = this.browserSupportsAvif ? "avif" : "webp";
            return {
                src: `${asset.preview}?&w=1000&format=${format}`,
                w: asset.width || 1000,
                h: asset.height || 1000,
                alt: ""
            };
        });
        const options = {
            dataSource: items,
            pswpModule: () => import("photoswipe")
        };
        if (this.lightbox) {
            this.lightbox.destroy();
        }
        this.lightbox = new PhotoSwipeLightbox(options);
        this.lightbox.on("change", () => {
            const index = this.lightbox.pswp.currIndex;
            const nextAsset = this.assetsAndVideos[index];
            this.selectItem(nextAsset);
        });
        this.lightbox.addFilter("thumbEl", () => {
            return this.mainPreview.nativeElement.querySelector("img");
        });
        this.lightbox.addFilter("placeholderSrc", () => {
            return this.mainPreview.nativeElement.querySelector("img")?.src;
        });
        this.lightbox.on("contentLoad", (e) => {
            const { content } = e;
            if (content.data.youtubeId && this.document) {
                e.preventDefault();
                content.element = this.document.createElement("div");
                const iframe = this.document.createElement("iframe");
                iframe.setAttribute("allowfullscreen", "");
                content.element.classList.add("flex", "items-center", "justify-center");
                iframe.classList.add("aspect-video", "w-full", "max-w-5xl");
                iframe.src = this.getYouTubeEmbedUrl(content.data.youtubeId, false);
                iframe.allowFullscreen = true;
                content.element.appendChild(iframe);
            }
        });
        this.lightbox.on("close", () => this.changeDetector.markForCheck());
        this.lightbox.init();
        this.lightbox.loadAndOpen(index);
    }

    imageLoaded(asset: AssetWithDimensions) {
        this._isLoadingAsset.next(false);
        this.selectedAssetIsTall = this.isTall(asset);
    }

    private isTall(asset: AssetWithDimensions): boolean {
        return 1.2 < asset.height / asset.width;
    }

    private getYouTubeEmbedUrl(videoId: string): SafeResourceUrl;
    private getYouTubeEmbedUrl(videoId: string, safe: false): string;
    private getYouTubeEmbedUrl(videoId: string, safe = true): SafeResourceUrl | string {
        const url = `https://www.youtube-nocookie.com/embed/${videoId}?modestbranding=1&rel=0`;
        return safe ? this.domSanitizer.bypassSecurityTrustResourceUrl(url) : url;
    }

    private checkAvifSupport() {
        if (isPlatformServer(this.platformId)) {
            return;
        }
        const AVIF = new Image();
        AVIF.onload = () => (this.browserSupportsAvif = true);
        AVIF.onerror = () => (this.browserSupportsAvif = false);
        AVIF.src =
            "data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A=";
    }
}
